/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import styled from "styled-components";
import type { ICptCodeData } from "types/precalculatedAllowables.types";

import {
  IChargeBasedPricingCalculation,
  IClaim,
  IInpatientOutlierCalculation,
  IInpatientOutlierPerCodeRule,
  IInpatientOutlierRule,
  ILesserOfChargesCalculation,
  ILineItem,
} from "../../types/claim";
import {
  DRG_BREAKDOWN_FIELD_LABELS,
  DrgBreakdownFieldKey,
} from "../../types/InpatientRuleset";

interface IOutlierCalculation {
  inpatientRulesetId?: string;
  type?: string;
  costOutlierMethod?: string;
  costToChargeRatio: number;
  fixedLossThresholdInCents: number;
  outlierPercent: number;
  estimatedCostInCents: number;
  triggerAmountInCents: number;
}

interface IPassThroughCodeDetail {
  code: string;
  inpatientRulesetId: string;
  chargeMultiplier: number;
  thresholdInCents: number;
  chargeInCents: number;
  calculatedAmountInCents: number;
}

interface IPassThroughCalculation {
  type: string;
  totalPassThroughAmountInCents: number;
  codes: IPassThroughCodeDetail[];
}

interface ILosOutlierCalculation {
  pricingRule: string;
  ruleApplied: boolean;
  ruleStatus: string;
  drgBaseRateInCents: number;
  alos: number | null;
  los: number | null;
  losSource: string | null;
  threshold?: number | null;
  multiplier?: number;
  outlierDays?: number;
  perDiemInCents?: number;
  outlierAddOnInCents?: number;
  expectedAllowedInCents?: number;
  dischargeStatus?: string;
  transferPaymentInCents?: number;
  drgCode?: string;
}

interface IStopLossCalculation {
  inpatientRulesetId: string;
  type: string;
  thresholdMultiplier: number;
  paymentPercent: number;
  thresholdAmountInCents: number;
  chargeAmountInCents: number;
}

interface IChargeOutlierCalculation {
  inpatientRulesetId: string;
  type: string;
  thresholdType: string;
  thresholdAmountInCents: number;
  outlierPercent: number;
  chargeFloorInCents?: number | null;
  chargeAmountInCents: number;
  edi837RawBaseExpected: number;
  excessChargesInCents?: number;
  chargeOutlierPaymentInCents?: number;
}

function isPerCodeRule(
  rule: IInpatientOutlierRule
): rule is IInpatientOutlierPerCodeRule {
  return rule.trigger.type === "PER_CODE_CHARGE_THRESHOLD";
}

// Human-friendly labels for INPATIENT_OUTLIER trigger / formula / mode enums.
// Mirrors the labels in ContractView.tsx — kept locally to avoid an extra import path.
const IPO_TRIGGER_LABEL: Record<string, string> = {
  STANDARD_CHARGE_THRESHOLD: "Charge Threshold",
  MODIFIED_CHARGE_THRESHOLD: "Modified Charge Threshold",
  LOS_RANGE: "LOS Range",
  PER_CODE_CHARGE_THRESHOLD: "Per-Code Charge Threshold",
  DISCHARGE_STATUS: "Discharge Status",
  ALWAYS: "Always",
  ADJUSTMENT_CODE: "Adjustment Code",
};
const IPO_FORMULA_LABEL: Record<string, string> = {
  PER_DIEM_X_LOS: "Per Diem × LOS",
  PER_DIEM_X_EXCESS_LOS: "Per Diem × Excess LOS",
  FLAT_RATE: "Flat Rate",
  PERCENT_OF_CHARGES: "Percent of Charges",
  PERCENT_OF_EXCESS_CHARGES: "Percent of Excess Charges",
  CASE_RATE_PLUS_PER_DIEM: "Case Rate + Per Diem",
  DRG_FIELD_SUM: "DRG Field Sum",
};
const IPO_MODE_LABEL: Record<string, string> = {
  REPLACE: "Replace base",
  ADDITIVE: "Add to base",
  MAX: "Max vs base",
};

interface ISpecialtyTabContentProps {
  cptCodes: ICptCodeData[];
  specialty: string;
  /** CLE-2173: per-CPT total RVU (facility), shown next to each code in the Calculation list so the ranking is self-explanatory. */
  rvuByCode?: Record<string, number>;
  claim?: IClaim; // Optional claim data for NCCI bypass display
  /**
   * Line items used to gate the multi-unit row-collapse logic. Episode claims
   * carry their lines under `episodeNetLineItems` rather than `claim.lineItems`,
   * so callers must resolve the right list and pass it explicitly; otherwise
   * the collapse never fires for episode-rooted claims.
   */
  lineItems?: ILineItem[];
  outlierPaymentInCents?: number | null;
  outlierCalculation?: IOutlierCalculation | null;
  /** Pass-through calculation data — rendered as additional calculation lines */
  passThroughCalculation?: IPassThroughCalculation | null;
  /** Total charge amount from the claim, used for outlier display */
  chargeAmountInCents?: number | null;
  /** LOS outlier / transfer calculation data from DRG pricing */
  losOutlierCalculation?: ILosOutlierCalculation | null;
  /** Stop loss calculation data (CLE-1897) */
  stopLossPaymentInCents?: number | null;
  stopLossCalculation?: IStopLossCalculation | null;
  /** Charge outlier calculation data (CLE-2120) */
  chargeOutlierPaymentInCents?: number | null;
  chargeOutlierCalculation?: IChargeOutlierCalculation | null;
  /** Inpatient outlier — composable trigger/formula/mode rule engine (CLE-2120 v3) */
  inpatientOutlierCalculation?: IInpatientOutlierCalculation | null;
  /** Lesser-of-charges cap — applied last, caps expected at billed charges × multiplier */
  lesserOfChargesCalculation?: ILesserOfChargesCalculation | null;
  /**
   * CLE-2295: Medicare/MA sequestration discount in cents applied to the claim's expected
   * server-side. Subtracted from the subtotal at the very end (after the lesser-of cap),
   * mirroring the aggregator. Only the first specialty tab receives a non-null value so the
   * whole-claim discount isn't double-counted across tabs.
   */
  sequestrationDiscountInCents?: number | null;
  /**
   * CLE-2600: the charge-based rule that priced this claim, when it priced per line
   * (`scope === "LINE_CHARGES"`) and carries `perLines`.
   *
   * Its charge-based lines are folded into the Calculation list as ordinary rows, and its
   * `pricedExpectedInCents` becomes the Subtotal — mirroring the server, where the override runs
   * after the lesser-of cap and replaces the expected outright
   * (`edi_conversion.service.ts:3640-3653`). Before this, a charge-based claim's per-line pricing
   * lived in a block stacked above this content and the Subtotal was suppressed, so the two halves
   * of the claim's pricing never appeared in one list or added up to one number.
   *
   * Only the first specialty tab receives a non-null value — the rule is whole-claim, and its
   * lines have no `providerProcedure` and therefore no specialty of their own. Same convention as
   * pass-through, the outliers and stop-loss.
   */
  chargeBasedPricingCalculation?: IChargeBasedPricingCalculation | null;
}

const Container = styled.div`
  padding: 20px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const PlanLabel = styled.strong`
  font-size: 16px;
  font-weight: 600;
`;

const PlanInfo = styled.span`
  font-size: 16px;
  color: #777;
  margin-left: 4px;
`;

const ContractedRatesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const ContractedRateItem = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    font-weight: 600;
    color: #333;
  }

  span {
    color: #777;
  }
`;

const VerticalBar = styled.span`
  color: #e0e0e0;
  font-size: 14px;
`;

const CalculationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CalculationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const CalculationLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  strong {
    font-weight: 600;
    color: #333;
  }

  span {
    color: #777;
  }
`;

const CalculationBullet = styled.span`
  color: #777;
  font-size: 14px;
`;

const CalculationAmount = styled.div`
  min-width: 120px;
  text-align: right;
  font-weight: 500;
  color: #333;
`;

// CLE-2600 — the contract-level sentence the Charge-Based Pricing block used to carry. It
// explains why one row in the list is priced a different way from the rest, which no per-row
// label can say on its own.
const ChargeBasedNote = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: #777;
  line-height: 1.5;
`;

const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-top: 16px;
  border-top: 2px solid #333;
  font-size: 16px;
  font-weight: 600;
`;

const SubtotalLabel = styled.div`
  color: #333;
`;

const SubtotalAmount = styled.div`
  color: #333;
  font-size: 18px;
`;

const OutlierSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
`;

const OutlierSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
`;

const OutlierDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  margin-bottom: 8px;
`;

const OutlierDetailLabel = styled.div`
  color: #777;
`;

const OutlierDetailAmount = styled.div`
  min-width: 120px;
  text-align: right;
  color: #333;
`;

/**
 * Format cents to currency string
 * @param cents - Amount in cents
 * @returns Formatted currency string (e.g., "$150.00")
 */
function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "N/A";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}
// LOS_RANGE trigger operators, expressed as math symbols for the audit row.
const IPO_LOS_OPERATOR_SYMBOL: Record<string, string> = {
  GT: ">",
  GTE: "≥",
  LT: "<",
  LTE: "≤",
  EQ: "=",
};

// Format the amount column of the formula row based on mode:
// ADDITIVE rolls into base ("+"), MAX competes with base ("→ max with base"),
// REPLACE overrides everything ("→ replaces base"). The amount itself is
// always the rule's calculated contribution.
function formatModeAmountLabel(mode: "ADDITIVE" | "MAX" | "REPLACE"): {
  prefix: string;
  suffix: string;
} {
  if (mode === "REPLACE") return { prefix: "→ ", suffix: " (replaces base)" };
  if (mode === "MAX") return { prefix: "", suffix: " (max vs base)" };
  return { prefix: "+", suffix: "" };
}

interface IInpatientOutlierRuleCardProps {
  rule: IInpatientOutlierRule;
  /**
   * CLE-2303 follow-up: when a sibling REPLACE rule fired on the same claim,
   * any ADDITIVE / MAX rule's contribution is discarded from the final
   * expected. Render those cards visually demoted (strikethrough amount,
   * dimmed text, "Overridden" pill instead of "Fired") so a reader auditing
   * the panel can see at a glance that those numbers do NOT roll up.
   */
  supersededByReplace?: boolean;
}

function InpatientOutlierRuleCard({
  rule,
  supersededByReplace = false,
}: IInpatientOutlierRuleCardProps) {
  const triggerLabel =
    IPO_TRIGGER_LABEL[rule.trigger.type] ?? rule.trigger.type;
  const modeLabel = IPO_MODE_LABEL[rule.mode] ?? rule.mode;
  const { prefix: amtPrefix, suffix: amtSuffix } = formatModeAmountLabel(
    rule.mode
  );

  // Visual demotion when a sibling REPLACE rule won. The math underneath
  // is unchanged — we just stop drawing the row in the "live, contributing"
  // orange and switch it to a muted-gray strikethrough so the screen no
  // longer reads as "this rule added $X" when it didn't.
  const supersededAmountStyle: React.CSSProperties | undefined =
    supersededByReplace
      ? { color: "#9ca3af", fontWeight: 600, textDecoration: "line-through" }
      : undefined;
  const supersededLabelStyle: React.CSSProperties | undefined =
    supersededByReplace ? { color: "#9ca3af" } : undefined;
  const firedPillText = supersededByReplace ? "Overridden" : "Fired";

  // Per-code path: render the whole-rule summary + per-line audit.
  if (isPerCodeRule(rule)) {
    return (
      <OutlierSection>
        <OutlierSectionTitle style={supersededLabelStyle}>
          {rule.rulesetName} ({modeLabel})
        </OutlierSectionTitle>
        <OutlierDetailRow>
          <OutlierDetailLabel style={supersededLabelStyle}>
            Trigger: {triggerLabel}
          </OutlierDetailLabel>
          <OutlierDetailAmount style={supersededLabelStyle}>
            {firedPillText}
          </OutlierDetailAmount>
        </OutlierDetailRow>
        {typeof rule.trigger.codeChargeFloorInCents === "number" && (
          <OutlierDetailRow>
            <OutlierDetailLabel style={supersededLabelStyle}>
              Per-line charge floor
            </OutlierDetailLabel>
            <OutlierDetailAmount style={supersededLabelStyle}>
              {formatCurrency(rule.trigger.codeChargeFloorInCents)}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}
        {rule.trigger.onlyCodesWithoutFeeSchedule === true && (
          <OutlierDetailRow>
            <OutlierDetailLabel style={supersededLabelStyle}>
              Only codes without a fee schedule
            </OutlierDetailLabel>
            <OutlierDetailAmount style={supersededLabelStyle}>
              Yes
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}
        {rule.lines.map((line, lineIdx) => (
          <OutlierDetailRow
            // eslint-disable-next-line react/no-array-index-key
            key={`${rule.inpatientRulesetId}-line-${lineIdx}`}
          >
            <OutlierDetailLabel style={supersededLabelStyle}>
              {line.code}
              {" • "}
              {formatCurrency(line.chargeAmountInCents)}
              {!line.fired && line.reason ? ` (${line.reason})` : ""}
            </OutlierDetailLabel>
            <OutlierDetailAmount style={supersededLabelStyle}>
              {line.calculatedAmountInCents != null
                ? formatCurrency(line.calculatedAmountInCents)
                : "—"}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        ))}
        <OutlierDetailRow>
          <OutlierDetailLabel style={supersededLabelStyle}>
            Formula: {IPO_FORMULA_LABEL[rule.formula.type] ?? rule.formula.type}
            {amtSuffix}
            {supersededByReplace ? " — overridden by Replace rule" : ""}
          </OutlierDetailLabel>
          <CalculationAmount
            style={
              supersededAmountStyle ?? { color: "#92400e", fontWeight: 600 }
            }
          >
            {amtPrefix}
            {formatCurrency(rule.totalCalculatedAmountInCents)}
          </CalculationAmount>
        </OutlierDetailRow>
      </OutlierSection>
    );
  }

  // Per-claim path: trigger + formula audit rows.
  const t = rule.trigger;
  const f = rule.formula;
  const formulaLabel = IPO_FORMULA_LABEL[f.type] ?? f.type;
  const calc = f.calculatedAmountInCents;

  // Charge-threshold variants (STANDARD / MODIFIED) carry threshold + effective charges.
  const isChargeThresholdTrigger =
    t.type === "STANDARD_CHARGE_THRESHOLD" ||
    t.type === "MODIFIED_CHARGE_THRESHOLD";

  // Excess charges = effective − threshold (for percent-of-excess formula context).
  const excess =
    t.effectiveChargesInCents != null && t.resolvedThresholdInCents != null
      ? Math.max(0, t.effectiveChargesInCents - t.resolvedThresholdInCents)
      : null;

  return (
    <OutlierSection>
      <OutlierSectionTitle style={supersededLabelStyle}>
        {rule.rulesetName} ({modeLabel})
      </OutlierSectionTitle>

      {/* Trigger row + trigger-specific audit */}
      <OutlierDetailRow>
        <OutlierDetailLabel style={supersededLabelStyle}>
          Trigger: {triggerLabel}
        </OutlierDetailLabel>
        <OutlierDetailAmount style={supersededLabelStyle}>
          {firedPillText}
        </OutlierDetailAmount>
      </OutlierDetailRow>

      {isChargeThresholdTrigger && t.resolvedThresholdInCents != null && (
        <OutlierDetailRow>
          <OutlierDetailLabel style={supersededLabelStyle}>
            Threshold
          </OutlierDetailLabel>
          <OutlierDetailAmount style={supersededLabelStyle}>
            {formatCurrency(t.resolvedThresholdInCents)}
          </OutlierDetailAmount>
        </OutlierDetailRow>
      )}
      {isChargeThresholdTrigger && t.effectiveChargesInCents != null && (
        <OutlierDetailRow>
          <OutlierDetailLabel style={supersededLabelStyle}>
            {t.type === "MODIFIED_CHARGE_THRESHOLD"
              ? "Modified Charges (× cost-to-charge ratio)"
              : "Billed Charges"}
          </OutlierDetailLabel>
          <OutlierDetailAmount style={supersededLabelStyle}>
            {formatCurrency(t.effectiveChargesInCents)}
          </OutlierDetailAmount>
        </OutlierDetailRow>
      )}
      {isChargeThresholdTrigger && excess != null && excess > 0 && (
        <OutlierDetailRow>
          <OutlierDetailLabel style={supersededLabelStyle}>
            Excess Charges
          </OutlierDetailLabel>
          <OutlierDetailAmount style={supersededLabelStyle}>
            {formatCurrency(excess)}
          </OutlierDetailAmount>
        </OutlierDetailRow>
      )}

      {/* ADJUSTMENT_CODE trigger (CLE-2303): which CARC codes were matched,
          the summed write-off they represent, and the dominance gate when
          configured. The dollar amount is the matched-adjustment total used
          for the dominance check, not the formula's calculated payment. */}
      {t.type === "ADJUSTMENT_CODE" &&
        t.matchedAdjustmentCodes != null &&
        t.matchedAdjustmentCodes.length > 0 && (
          <OutlierDetailRow>
            <OutlierDetailLabel style={supersededLabelStyle}>
              Matched codes
            </OutlierDetailLabel>
            <OutlierDetailAmount
              style={{
                fontFamily: "monospace",
                ...(supersededLabelStyle ?? {}),
              }}
            >
              {t.matchedAdjustmentCodes.join(", ")}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}
      {t.type === "ADJUSTMENT_CODE" &&
        t.matchedAdjustmentAmountInCents != null && (
          <OutlierDetailRow>
            <OutlierDetailLabel style={supersededLabelStyle}>
              Matched adjustment amount
            </OutlierDetailLabel>
            <OutlierDetailAmount style={supersededLabelStyle}>
              {formatCurrency(t.matchedAdjustmentAmountInCents)}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}
      {t.type === "ADJUSTMENT_CODE" &&
        typeof t.requireDominancePercent === "number" && (
          <OutlierDetailRow>
            <OutlierDetailLabel style={supersededLabelStyle}>
              Dominance threshold
            </OutlierDetailLabel>
            <OutlierDetailAmount style={supersededLabelStyle}>
              ≥ {t.requireDominancePercent}% of charges
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}

      {t.type === "LOS_RANGE" && (
        <OutlierDetailRow>
          <OutlierDetailLabel>
            {t.operator
              ? `Rule: LOS ${
                  IPO_LOS_OPERATOR_SYMBOL[t.operator] ?? t.operator
                } ${
                  t.resolvedDays != null
                    ? `${t.resolvedDays} days`
                    : "(unresolved)"
                }${
                  t.daysSource === "FEE_SCHEDULE"
                    ? " (from DRG outlier threshold)"
                    : ""
                }`
              : `LOS threshold: ${
                  t.resolvedDays != null
                    ? `${t.resolvedDays} days`
                    : "(unresolved)"
                }`}
          </OutlierDetailLabel>
          <OutlierDetailAmount>
            Actual {t.actualLos != null ? `${t.actualLos} days` : "—"}
          </OutlierDetailAmount>
        </OutlierDetailRow>
      )}

      {t.type === "DISCHARGE_STATUS" && (
        <>
          <OutlierDetailRow>
            <OutlierDetailLabel>Matched discharge status</OutlierDetailLabel>
            <OutlierDetailAmount>
              {t.matchedDischargeStatus ?? "—"}
            </OutlierDetailAmount>
          </OutlierDetailRow>
          {t.codes && t.codes.length > 0 && (
            <OutlierDetailRow>
              <OutlierDetailLabel>Allowed codes</OutlierDetailLabel>
              <OutlierDetailAmount style={{ fontFamily: "monospace" }}>
                {t.codes.join(", ")}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
        </>
      )}

      {t.type === "MODIFIED_CHARGE_THRESHOLD" &&
        t.costToChargeRatio != null && (
          <OutlierDetailRow>
            <OutlierDetailLabel style={supersededLabelStyle}>
              Cost-to-Charge Ratio
            </OutlierDetailLabel>
            <OutlierDetailAmount style={supersededLabelStyle}>
              {parseFloat(t.costToChargeRatio.toFixed(4))}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}

      {/* DRG_FIELD_SUM formula (CLE-2303): show which ProviderProcedure
          breakdown fields are being summed. Field labels mirror the
          rule-form multi-select so admins see the same labels they picked. */}
      {f.type === "DRG_FIELD_SUM" &&
        Array.isArray(f.fields) &&
        f.fields.length > 0 && (
          <OutlierDetailRow>
            <OutlierDetailLabel style={supersededLabelStyle}>
              Fields summed
            </OutlierDetailLabel>
            <OutlierDetailAmount style={supersededLabelStyle}>
              {f.fields
                .map(
                  (k) =>
                    DRG_BREAKDOWN_FIELD_LABELS[k as DrgBreakdownFieldKey] ?? k
                )
                .join(", ")}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}

      {/* Formula-specific audit (rows shown depend on formula.type) */}
      {f.type === "PER_DIEM_X_LOS" && (
        <>
          {f.perDiemInCents != null && (
            <OutlierDetailRow>
              <OutlierDetailLabel>
                Per Diem
                {f.perDiemSource && f.perDiemSource !== "MANUAL"
                  ? " (from DRG case rate ÷ ALOS)"
                  : ""}
              </OutlierDetailLabel>
              <OutlierDetailAmount>
                {formatCurrency(f.perDiemInCents)}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
          {f.losAdjustment && f.losAdjustment !== "NONE" && (
            <OutlierDetailRow>
              <OutlierDetailLabel>LOS adjustment</OutlierDetailLabel>
              <OutlierDetailAmount>
                {f.losAdjustment === "PLUS_1" ? "LOS + 1" : "LOS − 1"}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
          {f.capAtDrgRate && (
            <OutlierDetailRow>
              <OutlierDetailLabel>Capped at DRG rate</OutlierDetailLabel>
              <OutlierDetailAmount>Yes</OutlierDetailAmount>
            </OutlierDetailRow>
          )}
        </>
      )}
      {f.type === "PER_DIEM_X_EXCESS_LOS" && (
        <>
          {f.perDiemInCents != null && (
            <OutlierDetailRow>
              <OutlierDetailLabel>
                Per Diem
                {f.perDiemSource && f.perDiemSource !== "MANUAL"
                  ? " (from DRG case rate ÷ ALOS)"
                  : ""}
              </OutlierDetailLabel>
              <OutlierDetailAmount>
                {formatCurrency(f.perDiemInCents)}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
          {f.excessDays != null && (
            <OutlierDetailRow>
              <OutlierDetailLabel>
                Excess LOS
                {(() => {
                  if (f.excessLosBaseSource === "FEE_SCHEDULE")
                    return " (beyond DRG outlier threshold)";
                  if (f.excessLosBase != null)
                    return ` (beyond ${f.excessLosBase} days)`;
                  return "";
                })()}
              </OutlierDetailLabel>
              <OutlierDetailAmount>{f.excessDays} days</OutlierDetailAmount>
            </OutlierDetailRow>
          )}
        </>
      )}
      {f.type === "FLAT_RATE" && f.flatRateInCents != null && (
        <OutlierDetailRow>
          <OutlierDetailLabel>Flat Rate</OutlierDetailLabel>
          <OutlierDetailAmount>
            {formatCurrency(f.flatRateInCents)}
          </OutlierDetailAmount>
        </OutlierDetailRow>
      )}
      {f.type === "PERCENT_OF_CHARGES" &&
        f.effectiveChargesInCents != null &&
        t.effectiveChargesInCents == null && (
          // When the trigger doesn't carry effective charges (LOS_RANGE / DISCHARGE_STATUS / ALWAYS),
          // the formula falls back to whole-claim charges and stores them on its own result.
          <OutlierDetailRow>
            <OutlierDetailLabel>Billed Charges</OutlierDetailLabel>
            <OutlierDetailAmount>
              {formatCurrency(f.effectiveChargesInCents)}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        )}
      {f.type === "CASE_RATE_PLUS_PER_DIEM" && (
        <>
          {f.caseRateInCents != null && (
            <OutlierDetailRow>
              <OutlierDetailLabel>
                Case Rate
                {f.includedDays != null
                  ? ` (covers ${f.includedDays} days)`
                  : ""}
              </OutlierDetailLabel>
              <OutlierDetailAmount>
                {formatCurrency(f.caseRateInCents)}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
          {f.perDiemInCents != null && (
            <OutlierDetailRow>
              <OutlierDetailLabel>
                Per Diem (each day beyond included)
              </OutlierDetailLabel>
              <OutlierDetailAmount>
                {formatCurrency(f.perDiemInCents)}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
          {f.excessDays != null && (
            <OutlierDetailRow>
              <OutlierDetailLabel>Excess Days</OutlierDetailLabel>
              <OutlierDetailAmount>{f.excessDays} days</OutlierDetailAmount>
            </OutlierDetailRow>
          )}
        </>
      )}

      {/* Formula row with calculated amount and mode-aware prefix/suffix */}
      <OutlierDetailRow>
        <OutlierDetailLabel style={supersededLabelStyle}>
          Formula: {formulaLabel}
          {f.percent != null && ` (${f.percent}%)`}
          {amtSuffix}
          {supersededByReplace ? " — overridden by Replace rule" : ""}
        </OutlierDetailLabel>
        {calc != null ? (
          <CalculationAmount
            style={
              supersededAmountStyle ?? { color: "#92400e", fontWeight: 600 }
            }
          >
            {amtPrefix}
            {formatCurrency(calc)}
          </CalculationAmount>
        ) : (
          <OutlierDetailAmount style={{ color: "#6b7280" }}>
            {f.reason ?? "Not calculated"}
          </OutlierDetailAmount>
        )}
      </OutlierDetailRow>
    </OutlierSection>
  );
}

// Format the lesser-of-charges multiplier (e.g. 1.0 → "100%", 0.55 → "55%", 0.555 → "55.50%").
// Mirrors the percent-display style of the inpatient ruleset blocks (integer percent when whole,
// 2 decimals otherwise).
function formatMultiplierPercent(multiplier: number): string {
  const pct = multiplier * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}

interface ILesserOfChargesCardProps {
  calculation: ILesserOfChargesCalculation;
}

// Renders for a fired cap, or (CLE-3058) for a Line charges cap that evaluated
// case rate / package groups — the parent gates on `showLesserOfChargesCard`.
// The pill reads "Fired" when the cap lowered the expected, else "No reduction".
export function LesserOfChargesCard({
  calculation,
}: ILesserOfChargesCardProps) {
  const {
    ruleName,
    scope,
    multiplier,
    originalExpectedInCents,
    cappedExpectedInCents,
    totalChargeInCents,
    includedChargeInCents,
    capInCents,
    perLineCaps,
    groupCaps,
    fired,
  } = calculation;

  const title = `${ruleName ?? "Lesser-of-charges"} (${
    scope === "TOTAL_CHARGES" ? "Claim Total" : "Per Line"
  })`;

  // Reduction amount: the delta this rule applied to claim.expectedAmount.
  // Negative invariant guarded by the backend — clamp to 0 defensively.
  const rawReduction = originalExpectedInCents - cappedExpectedInCents;
  if (rawReduction < 0) {
    // eslint-disable-next-line no-console
    console.warn(
      "LesserOfChargesCard: negative reduction (cappedExpected > originalExpected)",
      calculation
    );
  }
  const reductionInCents = Math.max(0, rawReduction);

  const pctLabel = formatMultiplierPercent(multiplier);

  // Only show the "Included Charge" row when the backend supplied a separate
  // included subtotal that differs from the claim total — otherwise the row
  // is redundant (every line participates in the cap).
  const showIncludedRow =
    includedChargeInCents != null &&
    includedChargeInCents !== totalChargeInCents;

  return (
    <OutlierSection>
      <OutlierDetailRow>
        <OutlierSectionTitle style={{ marginBottom: 0, flex: 1 }}>
          {title}
        </OutlierSectionTitle>
        <OutlierDetailAmount
          style={{ color: "#92400e", fontWeight: 600, minWidth: "80px" }}
        >
          {fired ? "Fired" : "No reduction"}
        </OutlierDetailAmount>
        <CalculationAmount style={{ color: "#92400e", fontWeight: 600 }}>
          −{formatCurrency(reductionInCents)}
        </CalculationAmount>
      </OutlierDetailRow>

      {scope === "TOTAL_CHARGES" ? (
        <>
          <OutlierDetailRow>
            <OutlierDetailLabel>Multiplier</OutlierDetailLabel>
            <OutlierDetailAmount>{pctLabel}</OutlierDetailAmount>
          </OutlierDetailRow>
          <OutlierDetailRow>
            <OutlierDetailLabel>Total Charge Amount</OutlierDetailLabel>
            <OutlierDetailAmount>
              {formatCurrency(totalChargeInCents)}
            </OutlierDetailAmount>
          </OutlierDetailRow>
          {showIncludedRow && (
            <OutlierDetailRow>
              <OutlierDetailLabel>Included Charge</OutlierDetailLabel>
              <OutlierDetailAmount>
                {formatCurrency(includedChargeInCents)}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
          {capInCents != null && (
            <OutlierDetailRow>
              <OutlierDetailLabel>
                {showIncludedRow
                  ? "Cap (Mult × Included)"
                  : "Cap (Charges × Multiplier)"}
              </OutlierDetailLabel>
              <OutlierDetailAmount>
                {formatCurrency(capInCents)}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          )}
          <OutlierDetailRow>
            <OutlierDetailLabel>Original Expected</OutlierDetailLabel>
            <OutlierDetailAmount>
              {formatCurrency(originalExpectedInCents)}
            </OutlierDetailAmount>
          </OutlierDetailRow>
          <OutlierDetailRow>
            <OutlierDetailLabel>New Expected</OutlierDetailLabel>
            <OutlierDetailAmount style={{ fontWeight: 600 }}>
              {formatCurrency(cappedExpectedInCents)}
            </OutlierDetailAmount>
          </OutlierDetailRow>
        </>
      ) : (
        <>
          <OutlierDetailRow>
            <OutlierDetailLabel>Multiplier</OutlierDetailLabel>
            <OutlierDetailAmount>{pctLabel}</OutlierDetailAmount>
          </OutlierDetailRow>
          {/* One row per fired line — the backend only sends lines the cap
              actually changed. Same CPT can repeat for multi-unit claims. */}
          {(perLineCaps ?? []).map((line, idx) => (
            <OutlierDetailRow
              // eslint-disable-next-line react/no-array-index-key
              key={`loc-line-${line.cptCode}-${idx}`}
            >
              <OutlierDetailLabel>{line.cptCode}</OutlierDetailLabel>
              <OutlierDetailAmount>
                {formatCurrency(line.cappedExpectedInCents)}
              </OutlierDetailAmount>
            </OutlierDetailRow>
          ))}
          {/* CLE-3058 — case rate / package groups capped as one unit:
              capped = min(group expected, multiplier × group charges). */}
          {(groupCaps ?? []).map((group, idx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`loc-group-${group.rulesetId}-${idx}`}
              data-testid="lesser-of-group-cap"
            >
              <OutlierDetailRow>
                <OutlierDetailLabel style={{ fontWeight: 600 }}>
                  {group.ruleName ??
                    (group.ruleType === "PACKAGE"
                      ? "Package"
                      : "Case Rate")}{" "}
                  ({group.ruleType === "PACKAGE" ? "package" : "case rate"}{" "}
                  group: {group.codes.join(", ")})
                </OutlierDetailLabel>
              </OutlierDetailRow>
              <OutlierDetailRow>
                <OutlierDetailLabel>Group Expected</OutlierDetailLabel>
                <OutlierDetailAmount>
                  {formatCurrency(group.groupExpectedInCents)}
                </OutlierDetailAmount>
              </OutlierDetailRow>
              <OutlierDetailRow>
                <OutlierDetailLabel>Group Charges</OutlierDetailLabel>
                <OutlierDetailAmount>
                  {formatCurrency(group.groupChargeInCents)}
                </OutlierDetailAmount>
              </OutlierDetailRow>
              <OutlierDetailRow>
                <OutlierDetailLabel>Capped Amount</OutlierDetailLabel>
                <OutlierDetailAmount style={{ fontWeight: 600 }}>
                  {formatCurrency(group.cappedInCents)}
                </OutlierDetailAmount>
              </OutlierDetailRow>
            </div>
          ))}
        </>
      )}
    </OutlierSection>
  );
}

/**
 * Whether the claim modal shows the lesser-of card: when the cap lowered the
 * expected, or (CLE-3058) when a Line charges cap evaluated case rate / package
 * groups — those explain why a $1 secondary did not cap the surgery even when
 * nothing was lowered. Claims priced before CLE-3058 carry no `groupCaps`.
 */
export function showLesserOfChargesCard(
  calculation: ILesserOfChargesCalculation | null | undefined
): calculation is ILesserOfChargesCalculation {
  if (!calculation) return false;
  return calculation.fired || (calculation.groupCaps?.length ?? 0) > 0;
}

// Matches "2nd procedure", "267th procedure", etc. at the start of a description.
const ORDINAL_PROCEDURE_RE = /^(\d+)(st|nd|rd|th)\s+procedure\b(.*)$/i;

/**
 * If `description` starts with an ordinal like "2nd procedure",
 * return the ordinal token and the remainder (e.g. " is discounted by 50%, ...").
 * Otherwise return null.
 */
export function parseOrdinalProcedurePrefix(
  description: string
): { ordinal: string; rest: string } | null {
  const match = ORDINAL_PROCEDURE_RE.exec(description);
  if (!match) return null;
  return { ordinal: `${match[1]}${match[2].toLowerCase()}`, rest: match[3] };
}

// CLE-2957 — the suffix the precalc appends to an entry that stands for several
// units (`units` > 1). Stripped for grouping, so the entry collapses with the
// single-unit entries it was priced like.
const UNIT_TAIL_SUFFIX_RE =
  / \(applies to \d+ units: unit \d+ and the \d+ billed after it, priced alike\)$/;

/**
 * Group key for collapsing consecutive unit-based adjustment rows.
 * Two rows merge if their cptCode + adjustmentType match AND their description
 * matches after stripping any leading ordinal ("Nth procedure"). NCCI rows
 * (no ordinal) merge by exact description equality.
 */
export function descriptionGroupKey(description: string): string {
  return description
    .replace(UNIT_TAIL_SUFFIX_RE, "")
    .replace(ORDINAL_PROCEDURE_RE, "<ord> procedure$3");
}

function ordinalOf(n: number): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
  const suffix = ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[
    n % 10
  ];
  return `${n}${suffix ?? "th"}`;
}

/**
 * Component to display allowable amount calculation for a single specialty
 * Shows plan info, contracted rates, adjustment calculations, and subtotal
 */
export const SpecialtyTabContent: React.FC<ISpecialtyTabContentProps> =
  React.memo(
    ({
      cptCodes,
      specialty,
      rvuByCode = {},
      claim,
      lineItems,
      outlierPaymentInCents,
      outlierCalculation,
      passThroughCalculation,
      chargeAmountInCents,
      losOutlierCalculation,
      stopLossPaymentInCents,
      stopLossCalculation,
      chargeOutlierPaymentInCents,
      chargeOutlierCalculation,
      inpatientOutlierCalculation,
      lesserOfChargesCalculation,
      sequestrationDiscountInCents,
      chargeBasedPricingCalculation,
    }) => {
      // Get payer name from first provider procedure (they all have the same payer)
      const payerName = useMemo(() => {
        if (cptCodes.length === 0) return "";
        const firstProcedure = cptCodes[0]?.providerProcedures?.[0];
        return firstProcedure?.payerName || "";
      }, [cptCodes]);

      // Build contracted rates list: Array of {cptCode, providerName, rate}
      // Shows ALL providers for this specialty, not just the first one
      const contractedRates = useMemo(() => {
        const ratesList: Array<{
          cptCode: string;
          providerName: string;
          rate: number;
        }> = [];

        cptCodes.forEach((cptCode) => {
          cptCode.providerProcedures
            .filter((pp) => pp.provider.specialty === specialty)
            .forEach((pp) => {
              ratesList.push({
                cptCode: cptCode.code,
                providerName: pp.provider.name,
                rate: pp.priceInCents,
              });
            });
        });

        return ratesList;
      }, [cptCodes, specialty]);

      // Build calculation lines for ALL procedures from the highest-priced provider per CPT code
      // Shows both "paid at 100%" (no adjustments) and adjusted procedures
      // Sorted: 100% procedures first, then by procedureRank
      const calculationLines = useMemo(() => {
        interface ICalculationLine {
          cptCode: string;
          quantity: number;
          description: string;
          amountInCents: number;
          isFullPrice: boolean;
          procedureRank: number | null;
          priceInCents: number; // base price per unit
          adjustmentType?: string; // e.g. "NCCI" for NCCI edits
          collapsedCount?: number; // when set, this row represents N collapsed unit-rows
          collapsedUnits?: number; // billed units from the source line item (for "(units N)" label)
          collapsedStartOrdinal?: string; // e.g., "2nd"
          collapsedEndOrdinal?: string; // e.g., "266th"
          collapsedRest?: string; // ordinal-stripped tail of the description
        }

        const lines: ICalculationLine[] = [];

        cptCodes.forEach((cptCode) => {
          const providersForSpecialty = cptCode.providerProcedures.filter(
            (pp) => pp.provider.specialty === specialty
          );

          if (providersForSpecialty.length === 0) return;

          // Find the provider with the highest finalAmountInCents (same logic as subtotal)
          // Tie-breaker: alphabetical by provider name
          const highestPricedProvider = providersForSpecialty.reduce(
            (max, pp) => {
              if (pp.finalAmountInCents > max.finalAmountInCents) {
                return pp;
              }
              if (
                pp.finalAmountInCents === max.finalAmountInCents &&
                pp.provider.name.localeCompare(max.provider.name) < 0
              ) {
                return pp;
              }
              return max;
            }
          );

          // CLE-2829 — a "Modifier" adjustment is a PRICE STEP, not a billed unit. The engine
          // emits one per modifier on the line, chained: each `amountInCents` is the line's
          // running per-unit rate after that modifier, and every later ruleset (NCCI, MPDR, …)
          // already discounts from the last one. Counting them as units rendered a single 99214
          // billed "24>25" as two rows at the full rate and overstated the Subtotal, which is
          // summed from these rows (prod HMA1008037400: $1,264.72 against a $1,065.67 expected).
          const modifierAdjustments = highestPricedProvider.adjustments.filter(
            (adj) => adj.type === "Modifier"
          );
          const unitAdjustments = highestPricedProvider.adjustments.filter(
            (adj) => adj.type !== "Modifier"
          );
          const lastModifier =
            modifierAdjustments[modifierAdjustments.length - 1];
          // CLE-2957 — an entry may stand for several units (`units`); absent means one.
          const adjustedUnits = unitAdjustments.reduce(
            (n, adj) => n + (adj.units ?? 1),
            0
          );
          const fullPriceUnits = cptCode.quantity - adjustedUnits;

          // Show the units no ruleset discounted, if any exist
          // This handles both cases:
          // 1. No unit adjustments at all (all units at the full rate)
          // 2. Some unit adjustments but quantity > adjustments (remaining units at the full rate)
          if (fullPriceUnits > 0 && lastModifier) {
            // The full rate here is the modifier-adjusted one: one row for the code, carrying
            // every modifier's note.
            const modifierNotes = modifierAdjustments
              .map((adj) => adj.description)
              .join("; ");
            lines.push({
              cptCode: cptCode.code,
              quantity: fullPriceUnits,
              description:
                fullPriceUnits > 1
                  ? `${modifierNotes} × ${fullPriceUnits}`
                  : modifierNotes,
              amountInCents: lastModifier.amountInCents * fullPriceUnits,
              isFullPrice: false,
              procedureRank: null,
              priceInCents: highestPricedProvider.priceInCents,
              adjustmentType: "Modifier",
            });
          } else if (fullPriceUnits > 0) {
            lines.push({
              cptCode: cptCode.code,
              quantity: fullPriceUnits,
              description: "Paid at 100%",
              amountInCents:
                highestPricedProvider.priceInCents * fullPriceUnits,
              isFullPrice: true,
              procedureRank: null,
              priceInCents: highestPricedProvider.priceInCents,
            });
          }

          // Show each adjustment (discounted units)
          // CLE-2812 — show each adjustment exactly as the server priced it. The claim's calculatedData
          // is already NCCI-bypass-aware (a lifted edit is simply absent from it), so nothing is restored
          // here. This used to repaint every NCCI line at full price whenever the manual toggle was on,
          // whatever the server priced: prod A2458V1P5 read $2,850.53 against an $848.81 expected.
          unitAdjustments.forEach((adj) => {
            lines.push({
              cptCode: cptCode.code,
              quantity: adj.units ?? 1, // A non-Modifier adjustment is one unit, or `units` (CLE-2957)
              description: adj.description,
              amountInCents: adj.amountInCents,
              isFullPrice: false,
              procedureRank: adj.procedureRank,
              priceInCents: highestPricedProvider.priceInCents,
              adjustmentType: adj.type,
            });
          });
        });

        // Sort: 100% procedures first (sorted by CPT code), then adjusted procedures by rank
        const sortedLines = lines.sort((a, b) => {
          // Full price items come first
          if (a.isFullPrice && !b.isFullPrice) return -1;
          if (!a.isFullPrice && b.isFullPrice) return 1;

          // Within full price items, sort by CPT code
          if (a.isFullPrice && b.isFullPrice) {
            return a.cptCode.localeCompare(b.cptCode);
          }

          // Within adjusted items, sort by procedureRank (nulls last)
          const rankA = a.procedureRank ?? Infinity;
          const rankB = b.procedureRank ?? Infinity;
          if (rankA !== rankB) return rankA - rankB;

          // When ranks tie, push MUE rows after non-MUE rows so the bulk
          // "Units exceed..." rows render after the per-procedure pricing
          // adjustments.
          const aIsMue = (a.adjustmentType ?? "") === "MUE";
          const bIsMue = (b.adjustmentType ?? "") === "MUE";
          if (aIsMue !== bIsMue) return aIsMue ? 1 : -1;

          // Otherwise group remaining rank-ties by adjustmentType so any
          // same-type run (MPDR ordinals, NCCI pairs, etc.) stays contiguous.
          // Without this, two different types colliding on the same rank can
          // interleave and break the consecutive-run collapse — same class of
          // bug as the original MUE-vs-MPDR collision.
          const typeCmp = (a.adjustmentType ?? "").localeCompare(
            b.adjustmentType ?? ""
          );
          if (typeCmp !== 0) return typeCmp;

          // If same rank and same type, sort by CPT code
          return a.cptCode.localeCompare(b.cptCode);
        });

        // Collapse consecutive adjustment rows whose source lines include at
        // least one multi-unit billed line (e.g. 13160 with units=266). Rows
        // that share a CPT code only by virtue of being many separate single-
        // unit line items (the qty=4 case, all units=1) are left alone.
        // Per-code max + sum of billed units across line items. Two values:
        //   - max  : signals whether ANY line for this code has units > 1 (i.e.
        //            the code is genuinely multi-unit, not just qty-via-many-
        //            single-unit-lines like 31255 q=1 × 4).
        //   - sum  : the total billed units across all lines for this code,
        //            which is what the precalc aggregates into adjustment rows.
        // The collapse decision uses both: max >= 2 to gate, sum >= groupSize
        // to verify the group really came from multi-unit billing.
        const maxBilledUnitsPerCode = new Map<string, number>();
        const sumBilledUnitsPerCode = new Map<string, number>();
        // Episode claims keep their lines under `episodeNetLineItems`; the
        // caller resolves which list to use and passes it in via the `lineItems`
        // prop. Fall back to `claim.lineItems` for non-episode callers.
        const sourceLineItems = lineItems ?? claim?.lineItems;
        sourceLineItems?.forEach((li) => {
          const units = Math.max(
            Number(li.billedUnits) || 0,
            Number(li.quantity) || 0
          );
          const code = (li.cptCode ?? "").trim();
          if (!code) return;
          const prevMax = maxBilledUnitsPerCode.get(code) ?? 0;
          if (units > prevMax) maxBilledUnitsPerCode.set(code, units);
          const prevSum = sumBilledUnitsPerCode.get(code) ?? 0;
          sumBilledUnitsPerCode.set(code, prevSum + units);
        });

        const collapsed: ICalculationLine[] = [];
        let i = 0;
        while (i < sortedLines.length) {
          const head = sortedLines[i];
          if (head.isFullPrice) {
            collapsed.push(head);
            i += 1;
          } else {
            const groupKey = descriptionGroupKey(head.description);
            let j = i + 1;
            let extending = true;
            while (extending && j < sortedLines.length) {
              const next = sortedLines[j];
              const matches =
                !next.isFullPrice &&
                next.cptCode === head.cptCode &&
                (next.adjustmentType ?? "") === (head.adjustmentType ?? "") &&
                descriptionGroupKey(next.description) === groupKey;
              if (matches) {
                j += 1;
              } else {
                extending = false;
              }
            }

            const groupSize = j - i;
            // CLE-2957 — a row may stand for several units; count units, not rows.
            const groupUnits = sortedLines
              .slice(i, j)
              .reduce((sum, l) => sum + l.quantity, 0);
            const maxUnits = maxBilledUnitsPerCode.get(head.cptCode) ?? 0;
            const sumUnits = sumBilledUnitsPerCode.get(head.cptCode) ?? 0;
            // Collapse only when (a) at least one billed line for this code
            // has units > 1 — proving the rows came from multi-unit billing,
            // not from many separate single-unit lines (qty=4 case) — AND
            // (b) the total billed units cover the group.
            const willCollapse =
              groupSize > 1 && maxUnits >= 2 && sumUnits >= groupUnits;
            if (willCollapse) {
              const groupSlice = sortedLines.slice(i, j);
              const totalAmount = groupSlice.reduce(
                (sum, l) => sum + l.amountInCents,
                0
              );
              const firstOrd = parseOrdinalProcedurePrefix(head.description);
              const last = sortedLines[j - 1];
              const lastOrd = parseOrdinalProcedurePrefix(last.description);
              // A last row standing for N units ends N - 1 ordinals later.
              const lastOrdNumber = lastOrd
                ? parseInt(lastOrd.ordinal, 10)
                : NaN;
              const endOrdinal =
                lastOrd && last.quantity > 1 && !Number.isNaN(lastOrdNumber)
                  ? ordinalOf(lastOrdNumber + last.quantity - 1)
                  : lastOrd?.ordinal;
              collapsed.push({
                ...head,
                amountInCents: totalAmount,
                collapsedCount: groupUnits,
                collapsedUnits: groupUnits,
                collapsedStartOrdinal: firstOrd?.ordinal,
                collapsedEndOrdinal: endOrdinal,
                collapsedRest: firstOrd?.rest,
              });
            } else {
              for (let k = i; k < j; k += 1) collapsed.push(sortedLines[k]);
            }
            i = j;
          }
        }

        // CLE-2600 — fold the charge-based rule's own lines into this list.
        //
        // These lines have no `providerProcedure`, so nothing above can produce them: a
        // ONLY_IF_NO_FEE_SCHEDULE line is charge-based precisely BECAUSE the fee schedule has no
        // rate for it (0637 on prod 434162XX001BB is in `missingCodes`). Until now they were
        // itemized in a separate block above this content, which is what made a charge-based
        // claim impossible to read as one calculation.
        //
        // A REPLACE_FEE_SCHEDULE rule is the one case where a charge-based line CAN also have a
        // precalc row. The rule replaced that row's price, so the row is superseded, not
        // additive — drop it rather than showing the code twice and summing both.
        const chargeBasedLines = (
          chargeBasedPricingCalculation?.perLines ?? []
        ).filter((perLine) => perLine.source === "CHARGE_BASED");
        if (chargeBasedLines.length === 0) return collapsed;

        const supersededCodes = new Set(
          chargeBasedLines
            .filter((perLine) => perLine.cptCode)
            .map((perLine) => perLine.cptCode as string)
        );
        const kept = collapsed.filter(
          (line) => !supersededCodes.has(line.cptCode)
        );

        chargeBasedLines.forEach((perLine) => {
          // Same formatter the block and the Claim Allowables Method column use, so one claim
          // never shows two spellings of its own multiplier. It also keeps float noise out:
          // 0.575 * 100 is 57.49999999999999 in JS, which this renders as "57.50%".
          // Lead with the rule type. Every neighbouring row reads "Paid at 100%", so a bare
          // "85% of billed charges" is easy to skim past as one more fee-schedule variant when it
          // is in fact a different pricing system — the thing the removed block used to make
          // obvious with its own heading.
          let description = `Charge-based pricing — ${formatMultiplierPercent(
            perLine.multiplier ?? 0
          )} of billed charges (${formatCurrency(perLine.chargeInCents)})`;
          // The per-line ceiling is this line's story, not the claim's — the claim-level cap has
          // its own card below. `capApplied` is true only when it reduced THIS line.
          if (perLine.capApplied) {
            description += `, capped at ${formatCurrency(
              perLine.maxAmountInCents ?? 0
            )}`;
          }
          kept.push({
            cptCode: perLine.cptCode ?? "",
            quantity: 1,
            description,
            amountInCents: perLine.pricedExpectedInCents,
            // Renders `description` verbatim instead of "Paid at 100%".
            isFullPrice: false,
            procedureRank: null,
            priceInCents: perLine.pricedExpectedInCents,
          });
        });

        return kept;
      }, [
        cptCodes,
        specialty,
        claim?.lineItems,
        lineItems,
        chargeBasedPricingCalculation,
      ]);

      const chargeBasedLineCount = useMemo(
        () =>
          (chargeBasedPricingCalculation?.perLines ?? []).filter(
            (perLine) => perLine.source === "CHARGE_BASED"
          ).length,
        [chargeBasedPricingCalculation]
      );

      // Subtotal = the calculation lines as displayed, which are the server's bypass-aware prices.
      const subtotal = useMemo(() => {
        return calculationLines.reduce((total, line) => {
          return total + line.amountInCents;
        }, 0);
      }, [calculationLines]);

      // CLE-2303 follow-up: when an INPATIENT_OUTLIER REPLACE rule fires, the
      // contracted-rates base and any ADDITIVE/MAX rule contributions are all
      // discarded from the final expected. Dim the base sections and tag any
      // non-REPLACE sibling rule cards so the panel reads as "this REPLACE
      // value is the answer" instead of "all these numbers contribute."
      const replaceWinningInCents =
        inpatientOutlierCalculation?.replaceInCents ?? null;
      const isReplaceWinning = replaceWinningInCents != null;

      if (
        cptCodes.length === 0 &&
        !passThroughCalculation?.codes?.length &&
        !losOutlierCalculation?.ruleApplied
      ) {
        return (
          <Container>
            <div>No data available for this specialty</div>
          </Container>
        );
      }

      return (
        <Container>
          {/* Plan Information Section */}
          {payerName && (
            <Section className="flex">
              <PlanLabel>Plan:</PlanLabel>
              <PlanInfo>{payerName}</PlanInfo>
            </Section>
          )}

          {/* When DRG pricing applies, hide precalc rates/lines and show DRG calculation only */}
          {losOutlierCalculation?.ruleApplied ? (
            <Section>
              <SectionTitle>Calculation:</SectionTitle>
              {(() => {
                const isTransfer =
                  losOutlierCalculation.pricingRule === "BCBST_DRG_TRANSFER";
                const isOutlier =
                  losOutlierCalculation.pricingRule ===
                    "BCBST_DRG_DAY_OUTLIER" &&
                  losOutlierCalculation.ruleStatus === "success";
                const isNoOutlier =
                  losOutlierCalculation.pricingRule ===
                    "BCBST_DRG_DAY_OUTLIER" &&
                  losOutlierCalculation.ruleStatus === "success_no_outlier";

                return (
                  <>
                    <OutlierSectionTitle>
                      {isTransfer &&
                        `Transfer Adjustment (Discharge Status: ${
                          losOutlierCalculation.dischargeStatus || "N/A"
                        })`}
                      {isOutlier && "LOS Day-Outlier Adjustment"}
                      {isNoOutlier && "DRG Pricing"}
                    </OutlierSectionTitle>

                    <OutlierDetailRow>
                      <OutlierDetailLabel>
                        DRG Base Rate
                        {losOutlierCalculation.drgCode &&
                          ` (${losOutlierCalculation.drgCode})`}
                      </OutlierDetailLabel>
                      <OutlierDetailAmount>
                        {formatCurrency(
                          losOutlierCalculation.drgBaseRateInCents
                        )}
                      </OutlierDetailAmount>
                    </OutlierDetailRow>
                    <OutlierDetailRow>
                      <OutlierDetailLabel>
                        Average Length of Stay (ALOS/GLOS)
                      </OutlierDetailLabel>
                      <OutlierDetailAmount>
                        {losOutlierCalculation.alos} days
                      </OutlierDetailAmount>
                    </OutlierDetailRow>
                    <OutlierDetailRow>
                      <OutlierDetailLabel>
                        Actual Length of Stay
                      </OutlierDetailLabel>
                      <OutlierDetailAmount>
                        {losOutlierCalculation.los} days
                      </OutlierDetailAmount>
                    </OutlierDetailRow>

                    {/* Transfer-specific rows */}
                    {isTransfer && (
                      <>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Per Diem (
                            {formatCurrency(
                              losOutlierCalculation.drgBaseRateInCents
                            )}{" "}
                            / {losOutlierCalculation.alos})
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Transfer Payment (
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}{" "}
                            ×{" "}
                            {losOutlierCalculation.perDiemInCents
                              ? Math.round(
                                  (losOutlierCalculation.transferPaymentInCents ??
                                    0) / losOutlierCalculation.perDiemInCents
                                )
                              : (losOutlierCalculation.los ?? 0) + 1}{" "}
                            days)
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              losOutlierCalculation.transferPaymentInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                      </>
                    )}

                    {/* LOS Outlier-specific rows */}
                    {isOutlier && (
                      <>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Threshold
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {losOutlierCalculation.threshold} days
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Days ({losOutlierCalculation.los} −{" "}
                            {losOutlierCalculation.threshold})
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {losOutlierCalculation.outlierDays} days
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Per Diem (
                            {(losOutlierCalculation.multiplier ?? 0.7) * 100}% ×{" "}
                            {formatCurrency(
                              losOutlierCalculation.drgBaseRateInCents
                            )}{" "}
                            / {losOutlierCalculation.alos})
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Add-On (
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}{" "}
                            × {losOutlierCalculation.outlierDays})
                          </OutlierDetailLabel>
                          <CalculationAmount
                            style={{ color: "#92400e", fontWeight: 600 }}
                          >
                            +
                            {formatCurrency(
                              losOutlierCalculation.outlierAddOnInCents ?? 0
                            )}
                          </CalculationAmount>
                        </OutlierDetailRow>
                      </>
                    )}

                    {/* No-outlier info */}
                    {isNoOutlier && (
                      <>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Threshold
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {losOutlierCalculation.threshold} days
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel style={{ color: "#166534" }}>
                            LOS within threshold — no adjustment
                          </OutlierDetailLabel>
                          <OutlierDetailAmount style={{ color: "#166534" }}>
                            {formatCurrency(
                              losOutlierCalculation.expectedAllowedInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                      </>
                    )}
                  </>
                );
              })()}

              {/* Pass-through lines alongside LOS calculation (e.g. implant codes) */}
              {passThroughCalculation?.codes &&
                passThroughCalculation.codes.length > 0 && (
                  <CalculationList style={{ marginTop: "12px" }}>
                    <OutlierSectionTitle>
                      Pass-Through Charges
                    </OutlierSectionTitle>
                    {passThroughCalculation.codes.map((codeDetail) => (
                      <CalculationRow key={`pt-${codeDetail.code}`}>
                        <CalculationLeft>
                          <strong>{codeDetail.code}</strong>
                          <CalculationBullet>•</CalculationBullet>
                          <span>
                            {formatCurrency(codeDetail.chargeInCents)} &times;{" "}
                            {parseFloat(codeDetail.chargeMultiplier.toFixed(4))}
                          </span>
                        </CalculationLeft>
                        <CalculationAmount>
                          {formatCurrency(codeDetail.calculatedAmountInCents)}
                        </CalculationAmount>
                      </CalculationRow>
                    ))}
                  </CalculationList>
                )}
            </Section>
          ) : (
            <>
              {/* Contracted Rates Section — only when fee schedule codes exist.
                  Dimmed when a REPLACE rule wins below (the contracted rate
                  doesn't contribute to the final expected in that case). */}
              {contractedRates.length > 0 && (
                <Section
                  style={isReplaceWinning ? { opacity: 0.45 } : undefined}
                >
                  <SectionTitle>Contracted rates:</SectionTitle>
                  <ContractedRatesList>
                    {contractedRates.map((item, idx) => (
                      <React.Fragment
                        key={`${item.cptCode}-${item.providerName}-${idx}`}
                      >
                        {idx > 0 && <VerticalBar>|</VerticalBar>}
                        <ContractedRateItem>
                          <strong>{item.cptCode}</strong>
                          <span>({item.providerName}):</span>
                          <span>{formatCurrency(item.rate)}</span>
                        </ContractedRateItem>
                      </React.Fragment>
                    ))}
                  </ContractedRatesList>
                </Section>
              )}

              {/* Calculation Section. When a REPLACE rule wins below, only the
                  base calculation (the CalculationList lines) is dimmed — the
                  banner, rule cards (including the REPLACE rule itself), and
                  the sequestration / lesser-of cap rows must stay at full
                  opacity so the reader can follow the actual final number. */}
              <Section>
                <div style={isReplaceWinning ? { opacity: 0.45 } : undefined}>
                  <SectionTitle>Calculation:</SectionTitle>
                  <CalculationList>
                    {calculationLines.map((line, idx) => {
                      const getDescription = () => {
                        if (!line.isFullPrice) {
                          let desc = line.description;
                          // For collapsed unit groups, replace the leading
                          // ordinal with a range and append the multiplier.
                          if (
                            line.collapsedCount &&
                            line.collapsedCount > 1 &&
                            line.collapsedStartOrdinal &&
                            line.collapsedEndOrdinal
                          ) {
                            const perUnit =
                              line.amountInCents / line.collapsedCount;
                            const rest = (line.collapsedRest ?? "").replace(
                              /^\s+is\s+/i,
                              " are "
                            );
                            const range = `${line.collapsedStartOrdinal}-${line.collapsedEndOrdinal}`;
                            const perUnitLabel = formatCurrency(perUnit);
                            const totalLabel = formatCurrency(
                              line.amountInCents
                            );
                            const product = `${perUnitLabel} * ${line.collapsedCount} = ${totalLabel}`;
                            desc = `${range} procedures${rest}, ${product}`;
                          }
                          return desc;
                        }
                        if (line.quantity > 1)
                          return `Paid at 100% × ${line.quantity}`;
                        return "Paid at 100%";
                      };

                      // CLE-2957 — an uncollapsed adjustment row can stand for
                      // several units; say how many.
                      let codeLabel = line.cptCode;
                      if (line.collapsedCount && line.collapsedUnits) {
                        codeLabel = `${line.cptCode} (units ${line.collapsedUnits})`;
                      } else if (
                        line.quantity > 1 &&
                        line.adjustmentType &&
                        line.adjustmentType !== "Modifier"
                      ) {
                        codeLabel = `${line.cptCode} (units ${line.quantity})`;
                      }

                      return (
                        <CalculationRow key={idx}>
                          <CalculationLeft>
                            <span
                              style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                              <strong>{codeLabel}</strong>
                              {rvuByCode[line.cptCode] > 0 && (
                                <span style={{ color: "#777", marginLeft: 4 }}>
                                  (RVU: {rvuByCode[line.cptCode].toFixed(2)})
                                </span>
                              )}
                            </span>
                            <CalculationBullet>•</CalculationBullet>
                            <span>{getDescription()}</span>
                          </CalculationLeft>
                          <CalculationAmount>
                            {formatCurrency(line.amountInCents)}
                          </CalculationAmount>
                        </CalculationRow>
                      );
                    })}

                    {/* Pass-through lines */}
                    {passThroughCalculation?.codes &&
                      passThroughCalculation.codes.length > 0 && (
                        <>
                          <OutlierSectionTitle
                            style={{ marginTop: 0, marginBottom: "4px" }}
                          >
                            Pass-Through Charges
                          </OutlierSectionTitle>
                          {passThroughCalculation.codes.map((codeDetail) => (
                            <CalculationRow key={`pt-${codeDetail.code}`}>
                              <CalculationLeft>
                                <strong>{codeDetail.code}</strong>
                                <CalculationBullet>•</CalculationBullet>
                                <span>
                                  {formatCurrency(codeDetail.chargeInCents)}{" "}
                                  &times;{" "}
                                  {parseFloat(
                                    codeDetail.chargeMultiplier.toFixed(4)
                                  )}
                                </span>
                              </CalculationLeft>
                              <CalculationAmount>
                                {formatCurrency(
                                  codeDetail.calculatedAmountInCents
                                )}
                              </CalculationAmount>
                            </CalculationRow>
                          ))}
                        </>
                      )}
                  </CalculationList>
                  {chargeBasedLineCount > 0 && (
                    <ChargeBasedNote>
                      Charge-Based Pricing: this contract prices{" "}
                      {chargeBasedLineCount === 1 ? "one line" : "some lines"}{" "}
                      as a percentage of billed charges. Lines it doesn&apos;t
                      cover keep their fee-schedule pricing.
                    </ChargeBasedNote>
                  )}
                </div>

                {/* High-Cost Outlier Calculation */}
                {outlierCalculation &&
                  outlierPaymentInCents != null &&
                  outlierPaymentInCents > 0 && (
                    <OutlierSection>
                      <OutlierSectionTitle>
                        High-Cost Outlier Calculation
                        {outlierCalculation.type === "FIXED_COST" &&
                          " (Fixed Cost)"}
                        {!outlierCalculation.type &&
                          outlierCalculation.costOutlierMethod ===
                            "FIXED_STOP_LOSS" &&
                          " (Fixed Stop-Loss)"}
                      </OutlierSectionTitle>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Estimated Cost (
                          {formatCurrency(
                            Math.round(
                              outlierCalculation.estimatedCostInCents /
                                outlierCalculation.costToChargeRatio
                            )
                          )}{" "}
                          ×{" "}
                          {parseFloat(
                            outlierCalculation.costToChargeRatio.toFixed(4)
                          )}{" "}
                          CCR)
                        </OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            outlierCalculation.estimatedCostInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Trigger (
                          {formatCurrency(
                            outlierCalculation.triggerAmountInCents -
                              outlierCalculation.fixedLossThresholdInCents
                          )}{" "}
                          +{" "}
                          {formatCurrency(
                            outlierCalculation.fixedLossThresholdInCents
                          )}{" "}
                          threshold)
                        </OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            outlierCalculation.triggerAmountInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      {outlierPaymentInCents != null &&
                        outlierPaymentInCents > 0 && (
                          <>
                            <OutlierDetailRow>
                              <OutlierDetailLabel>
                                Excess (
                                {formatCurrency(
                                  outlierCalculation.estimatedCostInCents
                                )}{" "}
                                −{" "}
                                {formatCurrency(
                                  outlierCalculation.triggerAmountInCents
                                )}
                                )
                              </OutlierDetailLabel>
                              <OutlierDetailAmount>
                                {formatCurrency(
                                  outlierCalculation.estimatedCostInCents -
                                    outlierCalculation.triggerAmountInCents
                                )}
                              </OutlierDetailAmount>
                            </OutlierDetailRow>
                            <OutlierDetailRow>
                              <OutlierDetailLabel>
                                Outlier Payment (
                                {formatCurrency(
                                  outlierCalculation.estimatedCostInCents -
                                    outlierCalculation.triggerAmountInCents
                                )}{" "}
                                × {outlierCalculation.outlierPercent}%)
                              </OutlierDetailLabel>
                              <CalculationAmount
                                style={{ color: "#92400e", fontWeight: 600 }}
                              >
                                +{formatCurrency(outlierPaymentInCents)}
                              </CalculationAmount>
                            </OutlierDetailRow>
                          </>
                        )}
                    </OutlierSection>
                  )}

                {/* Stop Loss Calculation (CLE-1897) */}
                {stopLossCalculation &&
                  stopLossPaymentInCents != null &&
                  stopLossPaymentInCents > 0 && (
                    <OutlierSection>
                      <OutlierSectionTitle>
                        Stop Loss Calculation
                        {stopLossCalculation.type &&
                          ` (${stopLossCalculation.type
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c: string) =>
                              c.toUpperCase()
                            )})`}
                      </OutlierSectionTitle>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Threshold (
                          {formatCurrency(
                            stopLossCalculation.thresholdAmountInCents /
                              (stopLossCalculation.thresholdMultiplier / 100)
                          )}{" "}
                          × {stopLossCalculation.thresholdMultiplier}%)
                        </OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            stopLossCalculation.thresholdAmountInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>Billed Charges</OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            stopLossCalculation.chargeAmountInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Stop Loss Payment (
                          {formatCurrency(
                            stopLossCalculation.chargeAmountInCents
                          )}{" "}
                          × {stopLossCalculation.paymentPercent}%)
                        </OutlierDetailLabel>
                        <CalculationAmount
                          style={{ color: "#92400e", fontWeight: 600 }}
                        >
                          {formatCurrency(stopLossPaymentInCents)}
                        </CalculationAmount>
                      </OutlierDetailRow>
                    </OutlierSection>
                  )}

                {/* Charge Outlier Calculation (CLE-2120) */}
                {chargeOutlierCalculation &&
                  chargeOutlierPaymentInCents != null &&
                  chargeOutlierPaymentInCents > 0 && (
                    <OutlierSection>
                      <OutlierSectionTitle>
                        Charge Outlier Calculation
                        {chargeOutlierCalculation.thresholdType &&
                          ` (${
                            chargeOutlierCalculation.thresholdType === "FLAT"
                              ? "Flat Threshold"
                              : "Multiplier Threshold"
                          })`}
                      </OutlierSectionTitle>
                      {chargeOutlierCalculation.thresholdType ===
                        "MULTIPLIER" && (
                        <OutlierDetailRow>
                          <OutlierDetailLabel>Base Expected</OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              chargeOutlierCalculation.edi837RawBaseExpected
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                      )}
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Threshold
                          {chargeOutlierCalculation.thresholdType ===
                            "MULTIPLIER" &&
                            ` (${(
                              chargeOutlierCalculation.thresholdAmountInCents /
                              chargeOutlierCalculation.edi837RawBaseExpected
                            ).toFixed(1)}×)`}
                        </OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            chargeOutlierCalculation.thresholdAmountInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>Billed Charges</OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            chargeOutlierCalculation.chargeAmountInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      {chargeOutlierCalculation.excessChargesInCents !=
                        null && (
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Excess Charges
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              chargeOutlierCalculation.excessChargesInCents
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                      )}
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Outlier Payment (
                          {chargeOutlierCalculation.outlierPercent}% of excess)
                        </OutlierDetailLabel>
                        <CalculationAmount
                          style={{ color: "#92400e", fontWeight: 600 }}
                        >
                          +{formatCurrency(chargeOutlierPaymentInCents)}
                        </CalculationAmount>
                      </OutlierDetailRow>
                    </OutlierSection>
                  )}

                {/* Inpatient Outlier Calculation — composable rules (CLE-2120 v3).
                    CLE-2303 follow-up: when a REPLACE rule fires, ADDITIVE / MAX
                    siblings' calculated amounts are discarded from the final
                    expected even though the audit blob still records them as
                    `fired: true`. Pass `supersededByReplace` to mark those cards
                    visually so the panel doesn't read as "two rules contributed"
                    when only the REPLACE result enters the subtotal. */}
                {isReplaceWinning && (
                  <OutlierSection
                    style={{
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fcd34d",
                      borderRadius: "4px",
                      padding: "10px 12px",
                      marginTop: "16px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#92400e",
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Replace rule overrides base</strong> — A{" "}
                      <em>Replace</em> mode rule skips the contracted-rate base
                      and any sibling Additive / Max rules. The Subtotal
                      reflects the Replace rule&apos;s value only (
                      {formatCurrency(replaceWinningInCents)} before
                      sequestration / lesser-of cap).
                    </div>
                  </OutlierSection>
                )}
                {inpatientOutlierCalculation?.rules
                  ?.filter((r) => r.fired)
                  .map((rule, idx) => (
                    <InpatientOutlierRuleCard
                      // eslint-disable-next-line react/no-array-index-key
                      key={`ipo-${rule.inpatientRulesetId}-${idx}`}
                      rule={rule}
                      supersededByReplace={
                        isReplaceWinning && rule.mode !== "REPLACE"
                      }
                    />
                  ))}

                {/* Lesser-of-charges cap — last block before subtotal, since
                    the cap runs at the very end of the pricing pipeline. Only
                    shown when the cap actually reduced expected (fired) or
                    evaluated grouped rules (CLE-3058); any other
                    resolved-but-not-fired rule is audit-only and stays hidden. */}
                {showLesserOfChargesCard(lesserOfChargesCalculation) && (
                  <LesserOfChargesCard
                    calculation={lesserOfChargesCalculation}
                  />
                )}

                {/* CLE-2295 / CLE-2534: Medicare/MA sequestration adjustment — applied
                    server-side after the lesser-of cap, last step before the claim's stored
                    expectedAmount. Present when the claim carries CO-253 with a positive amount.
                    The amount is the remit's own summed CO-253, not a configured percent, so the
                    label must not quote one — it varies per claim (CLE-2534 deleted the 1.6%
                    SequestrationRuleset). */}
                {!!sequestrationDiscountInCents &&
                  sequestrationDiscountInCents > 0 && (
                    <OutlierSection>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Medicare sequestration adjustment (CO-253)
                        </OutlierDetailLabel>
                        <CalculationAmount
                          style={{ color: "#92400e", fontWeight: 600 }}
                        >
                          −{formatCurrency(sequestrationDiscountInCents)}
                        </CalculationAmount>
                      </OutlierDetailRow>
                    </OutlierSection>
                  )}
              </Section>
            </>
          )}

          {/* Subtotal — always rendered. CLE-2600 removed the `hideSubtotal` escape hatch: it
              existed only because this number was the fee-schedule portion on a charge-based
              claim and would have contradicted the block stacked above. It is now the claim's
              expected amount on every claim, so there is nothing left to suppress. */}
          <SubtotalRow>
            <SubtotalLabel>Subtotal:</SubtotalLabel>
            <SubtotalAmount>
              {formatCurrency(
                (() => {
                  // Mirrors the server's finalExpectedBeforeCap rule
                  // (services edi_conversion.service.ts:2403-2412):
                  //
                  //   1. REPLACE wins outright — overrides even TRANSFER. The rule's
                  //      calculated amount becomes the final expected.
                  //   2. TRANSFER (BCBST_DRG_TRANSFER) overrides the base + add-ons.
                  //      ADDITIVE / MAX / stop-loss / pass-through / charge-outlier
                  //      are all ignored when transfer applies.
                  //   3. Otherwise: max(base+addons+ADDITIVE, stopLoss, MAX) — the
                  //      three independent alternatives compete and the largest wins.
                  //   4. Finally: the lesser-of-charges cap applies last (CLE-2177),
                  //      replacing the result with its post-cap value when fired.
                  const beforeCap = (() => {
                    const replaceInCents =
                      inpatientOutlierCalculation?.replaceInCents;
                    if (replaceInCents != null) return replaceInCents;

                    const transferApplied =
                      losOutlierCalculation?.ruleApplied &&
                      losOutlierCalculation?.pricingRule ===
                        "BCBST_DRG_TRANSFER";
                    if (transferApplied) {
                      return losOutlierCalculation.expectedAllowedInCents ?? 0;
                    }

                    // Non-transfer base: LOS-outlier-applied (non-transfer) substitutes
                    // its expectedAllowedInCents for the precalc subtotal — matches the
                    // server, where `expectedAmountInCents` is replaced with the
                    // LOS-adjusted value when the outlier fires.
                    const baseWithAddons =
                      (losOutlierCalculation?.ruleApplied
                        ? losOutlierCalculation.expectedAllowedInCents ?? 0
                        : subtotal) +
                      (passThroughCalculation?.totalPassThroughAmountInCents ??
                        0) +
                      (outlierPaymentInCents ?? 0) +
                      (chargeOutlierPaymentInCents ?? 0);

                    return Math.max(
                      baseWithAddons +
                        (inpatientOutlierCalculation?.aggregateAdditiveInCents ??
                          0),
                      stopLossPaymentInCents ?? 0,
                      inpatientOutlierCalculation?.aggregateMaxInCents ?? 0
                    );
                  })();

                  // CLE-2600 — the charge-based override runs AFTER the cap and replaces the
                  // expected outright (edi_conversion.service.ts:3640-3653), so it wins over
                  // everything above including the cap. `pricedExpectedInCents` IS the claim's
                  // expectedAmount when the rule fires, which is what makes this Subtotal the
                  // claim's real total rather than only its fee-schedule portion.
                  //
                  // Reconciles visibly with the rows above on prod 434162XX001BB:
                  //   $502.99 + $10.62 + $88.22 + $348.62 + $10.20  =  $960.65
                  //   less the lesser-of cap card's                    -$95.99
                  //                                                   = $864.66
                  if (chargeBasedPricingCalculation) {
                    return chargeBasedPricingCalculation.pricedExpectedInCents;
                  }
                  if (lesserOfChargesCalculation?.fired) {
                    return lesserOfChargesCalculation.cappedExpectedInCents;
                  }
                  return beforeCap;
                })() -
                  // CLE-2295: subtract the per-claim sequestration discount last, mirroring the
                  // aggregator order (applied after the lesser-of cap). When not applied this is
                  // undefined/null and the subtotal is unchanged.
                  (sequestrationDiscountInCents ?? 0)
              )}
            </SubtotalAmount>
          </SubtotalRow>
        </Container>
      );
    }
  );
